const SCRIPT_SRC = 'https://oto.direct/embed-widget.js';
const CHATBOT_SLUG = 'sanjoy-va';
const BASE_URL = 'https://oto.direct';
const INIT_TIMEOUT_MS = 8000;
const INIT_POLL_MS = 50;

export interface OTOChatbotWidgetApi {
  version: string;
  open: () => void;
  close: () => void;
  forceClose: () => void;
}

declare global {
  interface Window {
    OTOChatbotWidget?: OTOChatbotWidgetApi;
  }
}

let loadPromise: Promise<OTOChatbotWidgetApi> | null = null;

function waitForWidgetApi(): Promise<OTOChatbotWidgetApi> {
  if (window.OTOChatbotWidget?.open) {
    return Promise.resolve(window.OTOChatbotWidget);
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.OTOChatbotWidget?.open) {
        window.clearInterval(timer);
        resolve(window.OTOChatbotWidget);
        return;
      }
      if (Date.now() - startedAt > INIT_TIMEOUT_MS) {
        window.clearInterval(timer);
        reject(new Error('OTO Chatbot Widget failed to initialize'));
      }
    }, INIT_POLL_MS);
  });
}

/** Ensures the OTO embed script is present and initialized. */
export function ensureAskSanjoyWidget(): Promise<OTOChatbotWidgetApi> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('OTO Chatbot Widget requires a browser'));
  }

  if (window.OTOChatbotWidget?.open) {
    return Promise.resolve(window.OTOChatbotWidget);
  }

  if (loadPromise) return loadPromise;

  loadPromise = new Promise<OTOChatbotWidgetApi>((resolve, reject) => {
    const fail = (message: string) => {
      loadPromise = null;
      reject(new Error(message));
    };

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="embed-widget.js"]'
    );

    if (existing) {
      // Script tag exists but API never appeared — likely CSP/network block on a prior attempt.
      if (existing.dataset.otoFailed === '1') {
        existing.remove();
      } else {
        const onLoad = () => {
          waitForWidgetApi().then(resolve).catch((err) => fail(String(err.message || err)));
        };
        existing.addEventListener('load', onLoad, { once: true });
        existing.addEventListener(
          'error',
          () => {
            existing.dataset.otoFailed = '1';
            fail(
              'OTO Chatbot Widget script blocked or failed to load. Check CSP allows https://oto.direct'
            );
          },
          { once: true }
        );
        // Load may have already completed before listeners were attached.
        void waitForWidgetApi().then(resolve).catch(() => {
          /* wait for load/error events instead */
        });
        return;
      }
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.dataset.chatbotSlug = CHATBOT_SLUG;
    script.dataset.baseUrl = BASE_URL;
    script.onload = () => {
      waitForWidgetApi().then(resolve).catch((err) => fail(String(err.message || err)));
    };
    script.onerror = () => {
      script.dataset.otoFailed = '1';
      fail(
        'OTO Chatbot Widget script blocked or failed to load. Check CSP allows https://oto.direct'
      );
    };
    document.body.appendChild(script);
  });

  return loadPromise;
}

/** Opens the OTO Ask Sanjoy embed modal, loading the script on demand if needed. */
export function openAskSanjoyWidget(): void {
  void ensureAskSanjoyWidget()
    .then((widget) => widget.open())
    .catch((err) => console.warn(err instanceof Error ? err.message : err));
}
